import { Head, router, usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { productsPath, profilePath, profileStorePath, servicesPath } from "@travily/routes";
import type FamiliesStore from "@travily/types/pages/FamiliesStore";
import { SwooshBackground } from "@ui/components/Swoosh";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { useShare } from "@ui/hooks/use-share";
import { PROFILE_SWOOSH_COLORS } from "@/components/ProfileFrame/ProfileFrame";
import { familyProfileAccount } from "@/components/ProfileFrame/profile-account";
import { ProfileStoreView } from "@/components/ProfileStoreView/ProfileStoreView";
import { useFriendshipActions } from "@/hooks/use-friendship-actions";
import { useNavigateBack } from "@/hooks/use-navigate-back";
import { PageFrame } from "@/layouts/page-frame";
import type { SharedProps } from "@/types";

const FamilyStorePage = ({ family, products, services, can }: FamiliesStore) => {
  const { share } = useShare();
  const { current_account } = usePage<SharedProps>().props;
  const isOwnProfile = current_account?.handle === family.handle;
  const onBack = useNavigateBack(profilePath(family.handle));
  const friendshipActions = useFriendshipActions(family);

  const handleShareProfile = () => {
    share({
      title: family.display_name,
      text: t("web.families.show.share_text", { name: family.display_name }),
      url: `${location.origin}${profilePath(family.handle)}`,
    });
  };

  return (
    <PageFrame
      background={<SwooshBackground className="hidden md:block" color={PROFILE_SWOOSH_COLORS.Family} />}
      mobileTopBar={isOwnProfile ? undefined : <TopBarMobile onBack={onBack} title={`@${family.handle}`} />}
    >
      <Head title={t("web.families.store.meta_title", { name: family.display_name })}>
        <meta name="description" content={t("web.families.show.meta_description", { name: family.display_name })} />
      </Head>
      <ProfileStoreView
        account={familyProfileAccount(family)}
        canCreateListing={can.create_listing}
        onAddProduct={can.create_listing ? () => router.post(productsPath()) : undefined}
        onAddService={can.create_listing ? () => router.post(servicesPath()) : undefined}
        onShareProfile={handleShareProfile}
        products={products}
        services={services}
        tabs={{ profileHref: profilePath(family.handle), storeHref: profileStorePath(family.handle) }}
        {...friendshipActions}
      />
    </PageFrame>
  );
};

export default FamilyStorePage;
